import {useDispatch, useSelector} from 'react-redux';
import {IElementQuestion} from '../../../../incase-domain/elements/question/question';
import {IElementPhotoAnswerQuestion} from '../../../../incase-domain/elements/question/photoAnswerQuestion';
import {QuestionElementInteractions} from '../../../../incase-domain/proptypes/runfile';
import {useQuestionRemainingToast} from './common/useQuestionRemainingToast';
import {useSaveRunfile} from '../../../../hooks/useSaveRunfile';
import {pointsSelector} from '../../../../store/selectors/runfile.selectors';
import {gameConfigSelector, loadedPresentationSelector} from '../../../../store/selectors/presentation.selectors';
import {IElementQuestionAnswer, IQuestionAnswerType} from '../../../../incase-domain/elements/base/questionElementBase';
import {isQuestionRetriable, isQuestionRetryLimitReached} from '../../../../util/util';
import {getNewPointsMultiple} from '../../../../service/gamification.service';
import {IGameConfig} from '../../../../incase-domain/proptypes/gameConfig';
import {fetchQuestionReportAction} from '../../../../store/actions/analytics.actions';
import {handleElementsClickedAction} from '../../../../store/actions/chalkBoardElements.actions';
import {emitToastAction} from '../../../../store/actions/questionToast.actions';
import {IElementQuestionYesNoV2} from '../../../../incase-domain/elements/question/questionYesNoV2';
import {ELEMENT} from '../../../../incase-domain/elements/base/ElementTypes';

type QuestionElement = IElementQuestion | IElementPhotoAnswerQuestion | IElementQuestionYesNoV2;

function useQuestionTry<T extends QuestionElement>(): (element: T, elementInteractions: QuestionElementInteractions) => void {
    const dispatch = useDispatch();

    const {emitRemainingTriesToast} = useQuestionRemainingToast();
    const saveRunfile = useSaveRunfile();
    const points = useSelector(pointsSelector);
    const gameConfig = useSelector(gameConfigSelector);
    const presentation = useSelector(loadedPresentationSelector);

    function handleTry(element: T, elementInteractions: QuestionElementInteractions): void {
        const {answered} = elementInteractions;
        if (answered) {
            return;
        }

        const propsMarkedAnswers = elementInteractions.markedAnswers ? elementInteractions.markedAnswers : [];

        const correctMarkedAnswersNum = propsMarkedAnswers.filter(
            (markedAnswerIndex: number) => element.answers[markedAnswerIndex].type === 'correct'
        ).length;
        const debatableMarkedAnswersNum = propsMarkedAnswers.filter(
            (markedAnswerIndex: number) => element.answers[markedAnswerIndex].type === 'debatable'
        ).length;
        const wrongMarkedAnswersNum = propsMarkedAnswers.filter(
            (markedAnswerIndex: number) => element.answers[markedAnswerIndex].type === 'incorrect'
        ).length;

        const totalCorrect = element.answers.filter((answer: IElementQuestionAnswer) => answer.type === 'correct').length;

        const isRetryable = isQuestionRetriable(element, presentation) && element.type !== ELEMENT.YES_NO_QUESTION_V2;
        const isCorrect = isRetryable
            ? !wrongMarkedAnswersNum && correctMarkedAnswersNum === totalCorrect
            : !wrongMarkedAnswersNum && propsMarkedAnswers.length === totalCorrect;

        const clickedAnswers = propsMarkedAnswers.map((markedAnswerIndex: number) => `${element.id}-${markedAnswerIndex}`);

        let answerType: IQuestionAnswerType;
        if (wrongMarkedAnswersNum) {
            answerType = 'incorrect';
        } else if (debatableMarkedAnswersNum) {
            answerType = 'debatable';
        } else if (isRetryable && !element.singleCorrect && !isCorrect) {
            answerType = 'incorrect';
        } else {
            answerType = 'correct';
        }

        const newPoints = getNewPointsMultiple(
            points || 0,
            correctMarkedAnswersNum,
            wrongMarkedAnswersNum,
            gameConfig || ({} as IGameConfig),
            element.gameConfig
        );

        if (!isRetryable || isQuestionRetryLimitReached(elementInteractions, element, presentation, answered, isCorrect)) {
            if (element.showResults) {
                dispatch(fetchQuestionReportAction(element.id));
            }

            saveRunfile(
                {
                    clickedAnswers: propsMarkedAnswers,
                    markedAnswers: [],
                    correctAnswers: element.correctAnswers,
                    answered: true,
                    retriedAnswers: elementInteractions.retriedAnswers || [],
                },
                element.id,
                element.slideId,
                newPoints
            );
        } else {
            emitRemainingTriesToast(element, elementInteractions);
            saveRunfile(
                {retriedAnswers: [...(elementInteractions.retriedAnswers || []), {attempt: clickedAnswers}]},
                element.id,
                element.slideId,
                newPoints
            );
        }

        dispatch(handleElementsClickedAction([...clickedAnswers, element.id], !!element.clearAfterClicked));
        dispatch(emitToastAction({type: answerType, questionId: element.id}));
    }

    return handleTry;
}

export default useQuestionTry;
